import { Stacked } from '@statsbomb/kitbag-components';
import { Legend } from '@statsbomb/kitbag-datavis';
import { useFilters } from '@statsbomb/kitbag-global-football';
import { useAtomValue } from 'jotai';
import { useRef } from 'react';
import {
  isStartPlacementAtom,
  pitchMarkerColouringModeAtom,
  showArrowsAtom,
  showLegendAtom,
} from '@/atoms/visualisation';
import { SHOT_EVENT_TYPES } from '@/consts/visualisations';
import { PitchMarkerColouringMode } from '@/types/visualisation';
import { getSelectedEventTypes } from '@/utils/filters';
import { EventMapArrowMarkerLegend } from './EventMapArrowMarkerLegend';
import { EventMapMarkerLegend } from './EventMapMarkerLegend';
import { EventMapObvLegend } from './EventMapObvLegend';
import { EventMapShotMarkerLegend } from './EventMapShotMarkerLegend';
import { EventMapXgGradient } from './EventMapXgGradient';
import { EventMapXgLegend } from './EventMapXgLegend';

const getEventMapLegendComponent = (
  showArrows: boolean,
  showMarkers: boolean,
  pitchMarkerColouringMode: PitchMarkerColouringMode,
) => {
  if (pitchMarkerColouringMode === 'xg') {
    return <EventMapXgLegend />;
  }

  if (pitchMarkerColouringMode === 'obv') {
    return <EventMapObvLegend />;
  }

  if (showArrows && showMarkers) {
    return <EventMapArrowMarkerLegend withMarkers />;
  }

  return showArrows ? <EventMapArrowMarkerLegend /> : <EventMapMarkerLegend />;
};

const getShotMarkerColourScale = (pitchMarkerColouringMode: PitchMarkerColouringMode) =>
  pitchMarkerColouringMode === 'obv' ? <EventMapObvLegend /> : <EventMapXgGradient />;

const isShotFirst = (selectedEventTypes: string[]) => {
  const firstShotIndex = selectedEventTypes.findIndex(type => SHOT_EVENT_TYPES.includes(type));
  const firstOtherIndex = selectedEventTypes.findIndex(type => !SHOT_EVENT_TYPES.includes(type));

  return firstShotIndex !== -1 && (firstOtherIndex === -1 || firstShotIndex < firstOtherIndex);
};

export const EventMapLegend = ({
  hasShotEvents = false,
  hasNonShotEvents = false,
}: {
  hasShotEvents?: boolean;
  hasNonShotEvents?: boolean;
}) => {
  const legendRef = useRef<HTMLLegendElement>(null);
  const showLegend = useAtomValue(showLegendAtom);
  const showArrows = useAtomValue(showArrowsAtom);
  const showMarkers = useAtomValue(isStartPlacementAtom);
  const pitchMarkerColouringMode = useAtomValue(pitchMarkerColouringModeAtom);
  const [filters] = useFilters('main');

  if (!showLegend || pitchMarkerColouringMode === 'none') {
    return null;
  }

  const shotLegend = (
    <>
      {getShotMarkerColourScale(pitchMarkerColouringMode)}
      <EventMapShotMarkerLegend forArrows={showArrows} />
    </>
  );

  if (hasShotEvents && pitchMarkerColouringMode === 'default') {
    const eventLegend = hasNonShotEvents ? getEventMapLegendComponent(showArrows, showMarkers, 'default') : null;

    return (
      <Legend legendRef={legendRef}>
        <Stacked direction="column">
          {isShotFirst(getSelectedEventTypes(filters)) ? (
            <>
              {shotLegend}
              {eventLegend}
            </>
          ) : (
            <>
              {eventLegend}
              {shotLegend}
            </>
          )}
        </Stacked>
      </Legend>
    );
  }

  if (hasShotEvents) {
    return (
      <Legend legendRef={legendRef}>
        <Stacked direction="column">{shotLegend}</Stacked>
      </Legend>
    );
  }

  const eventMapLegendComponent = getEventMapLegendComponent(showArrows, showMarkers, pitchMarkerColouringMode);

  return <Legend legendRef={legendRef}>{eventMapLegendComponent}</Legend>;
};
